import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Confetti from "react-confetti";
import { MapPin, UtensilsCrossed, Share2, RotateCcw, Star, Clock, Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";

function getSharedMatchFromUrl(search) {
  const params = new URLSearchParams(search);
  const matchData = params.get("data");

  if (!matchData) {
    return null;
  }

  try {
    return JSON.parse(matchData);
  } catch (error) {
    console.error("Failed to parse shared match data:", error);
    return null;
  }
}

function buildSharedMatchUrl(origin, sessionId, match) {
  const params = new URLSearchParams({
    data: JSON.stringify({
      restaurant_name: match?.restaurant_name || "",
      restaurant_photo: match?.restaurant_photo || "",
      restaurant_rating: match?.restaurant_rating ?? "",
      restaurant_address: match?.restaurant_address || "",
      matched_by: match?.matched_by || [],
      match_count: match?.match_count || 0,
      elapsed_time: match?.elapsed_time || 60,
    }),
  });

  return `${origin}/match/${sessionId}?${params.toString()}`;
}

export default function MatchPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [match, setMatch] = useState(null);
  const [showConfetti, setShowConfetti] = useState(true);
  const [showShareCard, setShowShareCard] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const shareCardRef = useRef(null);

  useEffect(() => {
    const matchData = sessionStorage.getItem("latestMatch");
    if (matchData) {
      setMatch(JSON.parse(matchData));
    } else {
      const sharedMatch = getSharedMatchFromUrl(location.search);
      if (sharedMatch) {
        setMatch(sharedMatch);
      } else {
        toast.error("No match found");
        navigate(`/swipe/${sessionId}`);
        return;
      }
    }

    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener("resize", handleResize);

    const timer = setTimeout(() => setShowConfetti(false), 4000);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timer);
    };
  }, [location.search, sessionId, navigate]);

  const handleGetDirections = () => {
    const query = match?.restaurant_address 
      ? `${match.restaurant_name} ${match.restaurant_address}`
      : match?.restaurant_name || "restaurant";
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`, "_blank");
  };

  const handleViewMenu = () => {
    window.open(`https://www.google.com/search?q=${encodeURIComponent(match?.restaurant_name + " menu")}`, "_blank");
  };

  const handleShare = async () => {
    const elapsedTime = match?.elapsed_time || 60;
    const shareText = `We picked dinner in ${elapsedTime} seconds with ForkYeah! 🍴 Going to ${match?.restaurant_name}`;
    const shareUrl = buildSharedMatchUrl(window.location.origin, sessionId, {
      ...match,
      elapsed_time: elapsedTime,
    });

    if (navigator.share) {
      try {
        await navigator.share({
          title: "ForkYeah Match!",
          text: shareText,
          url: shareUrl,
        });
      } catch (error) {
        if (error.name !== "AbortError") {
          copyToClipboard(shareText + " " + shareUrl);
        }
      }
    } else {
      copyToClipboard(shareText + " " + shareUrl);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const handleNewSession = () => {
    sessionStorage.clear();
    navigate("/");
  };

  const handleRematch = () => {
    sessionStorage.removeItem("latestMatch");
    navigate(`/swipe/${sessionId}`);
  };

  if (!match) {
    return (
      <main className="app-container">
        <div className="flex-1 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-forkyeah-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </main>
    );
  }

  const elapsedTime = match?.elapsed_time || 60;

  return (
    <main className="app-container overflow-hidden">
      {/* Confetti */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          <Confetti
            width={windowSize.width}
            height={windowSize.height}
            recycle={false}
            numberOfPieces={250}
            colors={["#FF6B35", "#FFD166", "#10B981", "#ffffff", "#EF4444"]}
            gravity={0.3}
          />
        </div>
      )}

      <div className="flex-1 flex flex-col px-5 py-6 overflow-y-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: "spring", duration: 0.6 }}
          className="text-center mb-4"
        >
          <motion.h1
            className="text-4xl sm:text-5xl font-black tracking-tight"
            initial={{ y: -30 }}
            animate={{ y: 0 }}
            transition={{ type: "spring", delay: 0.1 }}
            data-testid="match-title"
          >
            <span className="text-forkyeah-main">Fork </span>
            <span className="text-forkyeah-primary">Yeah!</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-forkyeah-muted font-semibold mt-1"
          >
            You matched! 🎉
          </motion.p>
        </motion.div>

        {/* Restaurant Card */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.2, type: "spring" }}
          className="bg-white rounded-3xl overflow-hidden shadow-xl mb-4"
          data-testid="match-restaurant-card"
        >
          {/* Restaurant Image */}
          <div className="relative h-44">
            <img
              src={match.restaurant_photo || "https://images.pexels.com/photos/262978/pexels-photo-262978.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"}
              alt={match.restaurant_name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="absolute bottom-3 left-4 right-4">
              <h2 className="text-2xl font-black text-white drop-shadow-lg">
                {match.restaurant_name}
              </h2>
              {match.restaurant_rating && (
                <div className="flex items-center gap-1 mt-1">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="text-white font-bold text-sm">{match.restaurant_rating.toFixed(1)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Match Summary */}
          <div className="p-4">
            <div className="bg-forkyeah-accent/20 rounded-xl p-3 text-center">
              <p className="text-sm font-bold text-forkyeah-main">
                {match.match_count || 2} of {match.matched_by?.length || 2} people liked this
              </p>
              <p className="text-xs text-forkyeah-muted mt-0.5">
                {match.matched_by?.join(", ")}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Emotional copy */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center mb-4"
        >
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full">
            <Clock className="w-4 h-4" />
            <span className="text-sm font-bold">Done in {elapsedTime} seconds</span>
          </div>
          <p className="text-forkyeah-muted text-sm font-medium mt-2">
            No more debate. Dinner is decided.
          </p>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="space-y-2"
        >
          <button
            data-testid="get-directions-btn"
            onClick={handleGetDirections}
            className="btn-primary gap-2"
          >
            <MapPin className="w-5 h-5" strokeWidth={2.5} />
            Get directions
          </button>

          <button
            data-testid="view-menu-btn"
            onClick={handleViewMenu}
            className="btn-secondary gap-2"
          >
            <UtensilsCrossed className="w-5 h-5" strokeWidth={2.5} />
            View menu
          </button>

          <div className="flex gap-2">
            <button
              data-testid="share-match-btn"
              onClick={handleShare}
              className="flex-1 h-12 bg-white text-forkyeah-main rounded-xl font-bold flex items-center justify-center gap-2 shadow-md border border-gray-100 active:scale-95 transition-transform"
            >
              <Share2 className="w-4 h-4" strokeWidth={2.5} />
              Share
            </button>
            <button
              data-testid="rematch-btn"
              onClick={handleRematch}
              className="flex-1 h-12 bg-white text-forkyeah-muted rounded-xl font-bold flex items-center justify-center gap-2 shadow-md border border-gray-100 active:scale-95 transition-transform"
            >
              <RotateCcw className="w-4 h-4" strokeWidth={2.5} />
              Rematch
            </button>
          </div>

          <button
            data-testid="new-session-btn"
            onClick={handleNewSession}
            className="w-full h-10 text-forkyeah-muted text-sm font-semibold active:opacity-70"
          >
            Start new session
          </button>
        </motion.div>

        {/* Share Card Modal */}
        <AnimatePresence>
          {showShareCard && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6"
              onClick={() => setShowShareCard(false)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                className="bg-white rounded-3xl p-6 max-w-sm w-full"
                onClick={(e) => e.stopPropagation()}
                ref={shareCardRef}
              >
                <div className="relative h-40 rounded-2xl overflow-hidden mb-4">
                  <img
                    src={match.restaurant_photo || "https://images.pexels.com/photos/262978/pexels-photo-262978.jpeg"}
                    alt={match.restaurant_name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-3 left-3">
                    <h3 className="text-xl font-black text-white">{match.restaurant_name}</h3>
                  </div>
                </div>
                <p className="text-center text-forkyeah-main font-bold mb-2">
                  We picked dinner in {elapsedTime} seconds
                </p>
                <p className="text-center text-forkyeah-muted text-sm mb-4">
                  via ForkYeah
                </p>
                <button
                  onClick={handleShare}
                  className="btn-primary"
                >
                  Share this
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
}
